import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import PublicHome from "./pages/PublicHome";
import Requirements from "./pages/Requirements";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Apply from "./pages/Apply";
import Status from "./pages/Status";
import Dashboard from "./pages/Dashboard";
import StorePage from "./pages/Store";
import Products from "./pages/Products";
import ProductForm from "./pages/ProductForm";
import Subscription from "./pages/Subscription";
import Profile from "./pages/Profile";
import Support from "./pages/Support";
import VerificationCenter from "./pages/VerificationCenter";
import BankDetails from "./pages/BankDetails";
import CategoryRequests from "./pages/CategoryRequests";
import VendorMessages from "./pages/VendorMessages";
import Orders from "./pages/Orders";
import PickupRequests from "./pages/PickupRequests";
import Notifications from "./pages/Notifications";
import InstallPwaModal from "./components/InstallPwaModal";
import MobileBottomNav from "./components/MobileBottomNav";
import { MenuIcon, BellIcon, MobilePhoneIcon, LogOutIcon } from "./components/Icons";
import api from "./services/api";
import "./index.css";

const getDestinationByStatus = (status) => {
  if (status === "approved") return "/vendor/dashboard";
  if (status === "pending" || status === "rejected" || status === "suspended" || status === "blocked") return "/vendor/status";
  return "/vendor/apply";
};

function App() {
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("vendor_user");
      const token = localStorage.getItem("vendor_token");
      return token && cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchUserStatus = async () => {
    try {
      const token = localStorage.getItem("vendor_token");
      if (token) {
        const { data } = await api.get("/user/profile");
        if (data && data.user) {
          const updatedUser = {
            ...data.user,
            vendorStatus: data.user.vendorStatus || "none",
          };
          localStorage.setItem("vendor_user", JSON.stringify(updatedUser));
          setUser(updatedUser);
        } else {
          const cachedUser = JSON.parse(localStorage.getItem("vendor_user") || "null");
          if (cachedUser) {
            setUser(cachedUser);
          } else {
            setUser(null);
          }
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem("vendor_token");
        localStorage.removeItem("vendor_user");
        setUser(null);
      } else {
        const cachedUser = JSON.parse(localStorage.getItem("vendor_user") || "null");
        if (cachedUser && localStorage.getItem("vendor_token")) {
          setUser(cachedUser);
        } else {
          setUser(null);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserStatus();
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", background: "#fbfaf8", flexDirection: "column", gap: "16px" }}>
        <img src={require("./assets/logo.png")} alt="IM Online" style={{ height: "52px", objectFit: "contain" }} />
        <div className="spinner-border text-brand-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const handleLoginSuccess = (userData, token) => {
    const safeUser = {
      ...userData,
      vendorStatus: userData?.vendorStatus || "none",
    };
    localStorage.setItem("vendor_token", token);
    localStorage.setItem("vendor_user", JSON.stringify(safeUser));
    setUser(safeUser);
    fetchUserStatus();
  };

  const handleLogout = () => {
    localStorage.removeItem("vendor_token");
    localStorage.removeItem("vendor_user");
    setUser(null);
  };

  return (
    <Router>
      <InstallPwaModal />
      <Switch>
        <Route exact path="/"><PublicHome /></Route>
        <Route exact path="/vendor"><PublicHome /></Route>
        <Route exact path="/vendor/requirements"><Requirements /></Route>

        <Route exact path="/vendor/login">
          {user ? <Redirect to={getDestinationByStatus(user.vendorStatus)} /> : <Login onLoginSuccess={handleLoginSuccess} />}
        </Route>

        <Route exact path="/vendor/register">
          {user ? <Redirect to={getDestinationByStatus(user.vendorStatus)} /> : <Register onRegisterSuccess={handleLoginSuccess} />}
        </Route>

        <Route exact path="/vendor/signup">
          <Redirect to="/vendor/register" />
        </Route>

        <Route>
          {!user ? (
            <Redirect to="/vendor/login" />
          ) : (
            <div style={{ display: "flex", minHeight: "100vh", backgroundColor: "#fbfaf8" }}>
              {user.vendorStatus === "approved" && (
                <Sidebar
                  user={user}
                  onLogout={handleLogout}
                  isOpen={sidebarOpen}
                  onToggle={() => setSidebarOpen(!sidebarOpen)}
                  planName={null}
                />
              )}

              <div
                className={user.vendorStatus === "approved" ? "app-main-layout flex-grow-1" : "flex-grow-1"}
                style={{ minWidth: 0 }}
              >
                {user.vendorStatus === "approved" && (
                  <div className="mobile-top-bar">
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                      <button
                        className="mobile-burger"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        aria-label="Toggle navigation menu"
                      >
                        <MenuIcon size={20} color="#0f172a" />
                      </button>
                      <div style={{ minWidth: 0 }}>
                        <span style={{ fontWeight: "900", fontSize: "13.5px", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
                          {user.storeName || "IM Online Seller Center"}
                        </span>
                        <span style={{ fontSize: "10px", fontWeight: "750", color: "#166534", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#16a34a", display: "inline-block" }} />
                          Verified Seller
                        </span>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={() => window.triggerPwaInstall && window.triggerPwaInstall()}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "6px 10px",
                          borderRadius: "10px",
                          background: "rgba(168, 80, 80, 0.08)",
                          border: "1px solid rgba(168, 80, 80, 0.25)",
                          color: "#a85050",
                          fontWeight: "750",
                          fontSize: "11.5px",
                          cursor: "pointer"
                        }}
                        title="Add Seller App to Home Screen"
                      >
                        <MobilePhoneIcon size={14} color="#a85050" />
                        <span>Install App</span>
                      </button>
                      <a
                        href="/vendor/notifications"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          background: "#f8fafc",
                          border: "1.5px solid #e2e8f0",
                          textDecoration: "none",
                          color: "#0f172a"
                        }}
                        title="Notifications"
                      >
                        <BellIcon size={18} color="#0f172a" />
                      </a>
                      <button
                        type="button"
                        onClick={handleLogout}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          background: "#fef2f2",
                          border: "1.5px solid #fecaca",
                          color: "#dc2626",
                          cursor: "pointer",
                          padding: 0
                        }}
                        title="Logout from Seller Panel"
                        aria-label="Logout"
                      >
                        <LogOutIcon size={18} color="#dc2626" />
                      </button>
                    </div>
                  </div>
                )}

                <main className={user.vendorStatus === "approved" ? "vendor-app-main-content" : ""}>
                  <Switch>
                    {user.vendorStatus === "none" && (
                      <Switch>
                        <Route exact path="/vendor/apply"><Apply user={user} onApplySubmitted={fetchUserStatus} /></Route>
                        <Redirect to="/vendor/apply" />
                      </Switch>
                    )}
                    {(user.vendorStatus === "pending" || user.vendorStatus === "rejected" || user.vendorStatus === "suspended" || user.vendorStatus === "blocked") && (
                      <Switch>
                        <Route exact path="/vendor/status"><Status user={user} onLogout={handleLogout} onRefresh={fetchUserStatus} /></Route>
                        <Redirect to="/vendor/status" />
                      </Switch>
                    )}
                    {user.vendorStatus === "approved" && (
                      <Switch>
                        <Route exact path="/vendor/dashboard"><Dashboard user={user} onLogout={handleLogout} /></Route>
                        <Route exact path="/vendor/notifications"><Notifications user={user} /></Route>
                        <Route exact path="/vendor/store"><StorePage user={user} /></Route>
                        <Route exact path="/vendor/products"><Products user={user} /></Route>
                        <Route exact path="/vendor/products/new"><ProductForm user={user} /></Route>
                        <Route exact path="/vendor/products/:id/edit"><ProductForm user={user} /></Route>
                        <Route exact path="/vendor/category-requests"><CategoryRequests user={user} /></Route>
                        <Route exact path="/vendor/subscription"><Subscription user={user} /></Route>
                        <Route exact path="/vendor/verification"><VerificationCenter user={user} /></Route>
                        <Route exact path="/vendor/documents"><VerificationCenter user={user} /></Route>
                        <Route exact path="/vendor/bank"><BankDetails user={user} /></Route>
                        <Route exact path="/vendor/bank-payouts"><BankDetails user={user} /></Route>
                        <Route exact path="/vendor/profile"><Profile user={user} onLogout={handleLogout} /></Route>
                        <Route exact path="/vendor/messages"><VendorMessages user={user} /></Route>
                        <Route exact path="/vendor/support"><Support user={user} /></Route>
                        <Route exact path="/vendor/pickup-requests"><PickupRequests user={user} /></Route>
                        <Route exact path="/vendor/orders"><Orders user={user} /></Route>
                        <Redirect to="/vendor/dashboard" />
                      </Switch>
                    )}
                    <Redirect to={getDestinationByStatus(user.vendorStatus)} />
                  </Switch>
                </main>

                {user.vendorStatus === "approved" && (
                  <MobileBottomNav
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                    sidebarOpen={sidebarOpen}
                  />
                )}
              </div>
            </div>
          )}
        </Route>
      </Switch>
    </Router>
  );
}

export default App;
